import {
  useCreateTeamAppraisalMutation,
  useDraftTeamAppraisalMutation,
  useGetTeamAppraisalDetailsQuery,
} from "@/app/features/employee/performance/performance.api";
import { useGetAllDesignationsQuery } from "@/app/features/employee/emp-dashboard/emp-dashboard.api";
import CustomDrawer from "@/components/CustomDrawer";
import MultiFileUploader from "@/components/MultiFileUploader";
import useAuth from "@/hooks/useAuth";
import { getErrorMessage } from "@/lib/error";
import { toast } from "@/lib/toast";
import { getFileNameFromUrl } from "@/utils";
import { Button, Flex, Form, Input, InputNumber } from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import ManagerRecommendations from "./ManagerRecommendations";
import ManagerRattings from "./ManagerRattings";
import {
  getRecommendationFields,
  type RecommendationType,
} from "./recommendation.constants";
import SetupRecomendation from "./SetupRecomendation";
import WithLoader from "@/components/WithLoader";
import { cn } from "@/lib/cn";

// ──────── Types ────────
interface ManageTeamAppraisalProps {
  open: boolean;
  onClose: () => void;
  planName?: string;
  mappingId?: string;
  planId?: string;
  employeeId?: string;
  appraisalStatus?: string;
}

type FormType = {
  recommendation_type?: RecommendationType;
  ratings: {
    kpi_id: string;
    kpi_name: string;
    weightage: number;
    self_evaluation: string;
    self_rating: number | null;
    evaluation: string;
    rating: number | null;
  }[];
  manager_comment: string;
  attachments?: string[];
  // Dismissal
  last_working_day?: dayjs.Dayjs;
  // Incentive
  incentive_effective_month?: dayjs.Dayjs;
  incentive_type?: string;
  incentive_value?: string;
  incentive_base?: string;
  // Promotion
  suggested_designation_id?: string;
  new_salary?: number;
  promotion_effective_month?: dayjs.Dayjs;
  // Increment
  increment_effective_month?: dayjs.Dayjs;
  increment_type?: string;
  increment_value?: string;
  increment_base?: string;
};

// ──────── Component ────────
const ManageTeamAppraisal = ({
  open,
  onClose,
  planName,
  mappingId,
  planId,
  employeeId,
  appraisalStatus,
}: ManageTeamAppraisalProps) => {
  const isDraft = appraisalStatus === "DRAFT";
  const { userId, organizationId } = useAuth();
  const [form] = Form.useForm<FormType>();

  // Mutations
  const [draftAppraisal, { isLoading: isDrafting }] =
    useDraftTeamAppraisalMutation();
  const [submitAppraisal, { isLoading: isSubmitting }] =
    useCreateTeamAppraisalMutation();

  // Fetch Team Appraisal Details
  const { data: detailRes, isFetching: isDetailFetching } =
    useGetTeamAppraisalDetailsQuery(mappingId || "", {
      skip: !mappingId || !open,
    });

  const { data: designationsData, isLoading: isDesignationLoading } =
    useGetAllDesignationsQuery(
      { params: { status: "active" } },
      { skip: !open },
    );

  const employeeKpis = detailRes?.data?.employeeKpis || [];
  const managerMappings = detailRes?.data?.managerMappings || [];
  const readonlyManagerMappings = isDraft
    ? managerMappings.slice(0, -1)
    : managerMappings;

  const designationMap = useMemo(() => {
    const map: Record<string, string> = {};
    designationsData?.data?.data?.forEach((item: any) => {
      map[item.id] = item.name;
    });
    return map;
  }, [designationsData]);

  // Reset fields when drawer closes
  useEffect(() => {
    if (!open) form.resetFields();
  }, [open, form]);

  useEffect(() => {
    if (detailRes?.data) {
      const targetData = detailRes.data;
      const employeeKpis = detailRes?.data?.employeeKpis || [];
      const attachments = targetData.attachments || [];
      const managerMapping = targetData.managerMappings?.at(-1);
      const mr = managerMapping?.managerRecommendation;

      form.setFieldsValue({
        ratings: employeeKpis.map((k: any) => {
          const managerRating = k.managerRatings?.at(-1);
          return {
            kpi_id: k.id,
            kpi_name: k.kpiName,
            weightage: Number(k.weightage),
            self_evaluation: k.selfEvaluation || "",
            self_rating: k.selfRating || null,
            ...(isDraft && {
              evaluation: managerRating?.evaluation || "",
              rating: managerRating?.rating || null,
            }),
          };
        }),

        // ── Recommendation ──
        ...(mr &&
          isDraft && {
            recommendation_type:
              (mr.recommendationType as RecommendationType) ?? null,
            // Dismissal
            last_working_day: mr.lastWorkingDay
              ? dayjs(mr.lastWorkingDay)
              : undefined,
            // Incentive
            incentive_effective_month: mr.incentiveEffectiveMonth
              ? dayjs(mr.incentiveEffectiveMonth)
              : undefined,
            incentive_type: mr.incentiveType ?? "FLAT",
            incentive_value:
              mr.incentiveValue != null ? String(mr.incentiveValue) : undefined,
            incentive_base: mr.incentiveBase ?? undefined,
            // Promotion
            suggested_designation_id: mr.suggestedDesignationId ?? undefined,
            new_salary: mr.newSalary ?? undefined,
            promotion_effective_month: mr.promotionEffectiveMonth
              ? dayjs(mr.promotionEffectiveMonth)
              : undefined,
            // Increment
            increment_effective_month: mr.incrementEffectiveMonth
              ? dayjs(mr.incrementEffectiveMonth)
              : undefined,
            increment_type: mr.incrementType ?? "FLAT",
            increment_value:
              mr.incrementValue != null ? String(mr.incrementValue) : undefined,
            increment_base: mr.incrementBase ?? undefined,
          }),
        ...(isDraft && {
          manager_comment: managerMapping?.appraisalComment || "",
          attachments: attachments?.map((file: any) => file.fileUrl),
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailRes, form, open]);

  const handleSubmit = (type: "draft" | "submit") => {
    form
      .validateFields()
      .then(async (values) => {
        const payload = {
          mapping_id: mappingId || "",
          plan_id: planId || "",
          organization_id: organizationId || "",
          employee_id: employeeId || "",
          manager_id: userId || "",
          manager_comment: values.manager_comment,
          ratings: values.ratings.map((r) => ({
            kpi_id: r.kpi_id,
            rating: Number(r.rating),
            evaluation: r.evaluation,
          })),
          attachments:
            values.attachments?.map((url) => ({
              file_name: getFileNameFromUrl(url),
              file_url: url,
            })) || [],
        };

        // Merge the payload fields for every active recommendation section.
        const recommendationFields = getRecommendationFields(
          values.recommendation_type,
          values,
        );

        try {
          let res;
          if (type === "draft") {
            res = await draftAppraisal({
              ...payload,
              recommendation_type: values.recommendation_type,
              ...recommendationFields,
            }).unwrap();
          } else {
            res = await submitAppraisal({
              ...payload,
              recommendation_type: values.recommendation_type,
              ...recommendationFields,
            }).unwrap();
          }

          if (res?.success === false) {
            toast("error", getErrorMessage(res, `Failed to ${type} Appraisal`));
            return;
          }

          toast(
            "success",
            `Appraisal ${type === "draft" ? "drafted" : "submitted"} successfully`,
          );
          handleDrawerClose();
        } catch (error: any) {
          toast("error", getErrorMessage(error, `Failed to ${type} Appraisal`));
        }
      })
      .catch((err) => {
        console.error("Validation Failed:", err);
      });
  };

  const handleDrawerClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={handleDrawerClose}
      title={
        planName ? `${planName} - Team Appraisal` : "Manage Team Appraisal"
      }
    >
      <WithLoader
        isLoading={isDetailFetching}
        className={cn(
          "rounded-lg",
          isDetailFetching && "h-[calc(100vh_-_105px)]",
        )}
      >
        <Form
          form={form}
          layout="vertical"
          className="custom-form"
          size="large"
        >
          <>
            {/* ── KPI Ratings ── */}
            <div className="border border-theme-gray-700 rounded-xl p-6">
              <h4 className="text-white text-base font-semibold mb-6">
                Team Performance Appraisal{" "}
                <span className="text-red-500">*</span>
              </h4>

              <Form.List name="ratings">
                {(fields) => (
                  <>
                    {fields.map((field, index) => {
                      const { key, ...restField } = field;
                      return (
                        <div key={key} className="relative space-y-4">
                          {index > 0 && (
                            <div className="border-t border-dashed border-theme-gray-600 my-6" />
                          )}

                          <Flex justify="space-between" align="center">
                            <h5 className="text-white text-sm font-medium">
                              KPI -{index + 1}
                            </h5>
                          </Flex>

                          <div className="grid grid-cols-2 gap-6">
                            <Form.Item
                              {...restField}
                              label="KPI Name"
                              name={[field.name, "kpi_name"]}
                            >
                              <Input disabled className="bg-slate-800/50 text-sm" />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              label="Weightage(%)"
                              name={[field.name, "weightage"]}
                            >
                              <InputNumber
                                disabled
                                controls={false}
                                className="w-full bg-slate-800/50 text-sm"
                              />
                            </Form.Item>
                          </div>

                          {employeeKpis[index]?.details && (
                            <div className="mt-4">
                              <label className="text-white text-sm block mb-2">
                                Details
                              </label>
                              <div className="bg-theme-slate-800 border border-white/30 rounded-lg px-4 py-3 text-white text-sm min-h-[60px] whitespace-pre-wrap">
                                {employeeKpis[index].details}
                              </div>
                            </div>
                          )}

                          <div className="grid grid-cols-2 gap-6">
                            <Form.Item
                              {...restField}
                              label="Employee Self Evaluation"
                              name={[field.name, "self_evaluation"]}
                            >
                              <TextArea
                                rows={3}
                                disabled
                                className="bg-slate-800/30 text-sm"
                              />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              label="Rating Scale (1-5)"
                              name={[field.name, "self_rating"]}
                            >
                              <InputNumber
                                disabled
                                className="w-full bg-slate-800/30 text-sm"
                              />
                            </Form.Item>
                          </div>

                          {(() => {
                            const ratings =
                              employeeKpis[index]?.managerRatings || [];
                            // Draft: show all except the last as readonly; last goes into form
                            // Non-draft: show ALL as readonly
                            const readonlyRatings = isDraft
                              ? ratings.slice(0, -1)
                              : ratings;

                            return (
                              <ManagerRattings ratings={readonlyRatings} />
                            );
                          })()}

                          {/* Editable evaluation + rating — only in DRAFT mode */}

                          <div className="grid grid-cols-2 gap-6">
                            <Form.Item
                              {...restField}
                              label="Manager Evaluation"
                              name={[field.name, "evaluation"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Evaluation is required",
                                },
                              ]}
                            >
                              <TextArea
                                rows={4}
                                placeholder="Enter your evaluation"
                              />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              initialValue={1}
                              label="Manager Rating (1-5)"
                              name={[field.name, "rating"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Rating is required",
                                },
                                {
                                  type: "number",
                                  min: 1,
                                  max: 5,
                                  message: "Rating must be between 1 and 5",
                                },
                              ]}
                            >
                              <InputNumber
                                min={1}
                                max={5}
                                placeholder="0"
                                className="w-full"
                              />
                            </Form.Item>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </Form.List>

              {/* ── Employee Overall Comment ── */}
              {(detailRes?.data?.employeeComment || detailRes?.data?.employee_comment) && (
                <div className="mt-6 mb-6">
                  <label className="text-white text-sm block mb-2">
                    Employee Overall Comment
                  </label>
                  <div className="bg-theme-slate-800 border border-white/30 rounded-lg px-4 py-3 text-white text-sm min-h-[80px] whitespace-pre-wrap">
                    {detailRes.data.employeeComment || detailRes.data.employee_comment}
                  </div>
                </div>
              )}

              {/* ── Overall Manager Comment ── */}
              <Form.Item
                label="Overall Manager Comment"
                name="manager_comment"
                rules={[
                  { required: true, message: "Overall comment is required" },
                ]}
                className="mb-0 !mt-2"
              >
                <TextArea
                  rows={4}
                  placeholder="Enter overall manager comment"
                />
              </Form.Item>
            </div>

            {/* ── Attachments ── */}
            <Form.Item className="mt-8" name="attachments">
              <MultiFileUploader
                form={form}
                name="attachments"
                title="Drag & Drop your file here"
                description="Browse"
              />
            </Form.Item>

            {/* ── Previous Manager Evaluations ── */}
            {readonlyManagerMappings.length > 0 && (
              <div className="mt-8">
                <ManagerRecommendations
                  managerMappings={readonlyManagerMappings}
                  designationMap={designationMap}
                />
              </div>
            )}

            {/* ── Action Sections (mutually exclusive) ── */}
            <SetupRecomendation
              designations={designationsData?.data?.data}
              isDesignationLoading={isDesignationLoading}
            />

            {/* ── Footer Buttons ── */}
            <Flex justify="end" gap={12} className="mt-12">
              <Button
                size="large"
                className="btn-primary-bordered min-w-24"
                onClick={() => handleSubmit("draft")}
                loading={isDrafting}
              >
                Draft
              </Button>
              <Button
                size="large"
                className="btn-primary min-w-24"
                onClick={() => handleSubmit("submit")}
                loading={isSubmitting}
              >
                Submit
              </Button>
            </Flex>
          </>
        </Form>
      </WithLoader>
    </CustomDrawer>
  );
};

export default ManageTeamAppraisal;
